import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Button, IconButton, TextField,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Alert, CircularProgress, Chip, Tooltip, Divider
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon,
  Memory as CorePlatformIcon, Hub as ThirdPartyIcon,
  Visibility as ObservabilityIcon,
} from '@mui/icons-material';
import { get, authPost, authPut, authDelete } from '../../services/apiService';
import { isAdmin } from '../../services/authService';

const PRIMARY = '#1A2E22';
const PRIMARY_DARK = '#0F1A14';
const MUTED = '#666666';
const SURFACE = '#F8F9FA';

const PILLARS = [
  { key: 'corePlatform',  label: 'Core Platform & Runtime Health',      icon: CorePlatformIcon,  color: '#1d6fd4' },
  { key: 'thirdParty',    label: 'Third-Party Integration Reliability',  icon: ThirdPartyIcon,    color: '#e53935' },
  { key: 'observability', label: 'Safe Delivery',                        icon: ObservabilityIcon, color: '#f4511e' },
];

const inputSx = {
  '& .MuiOutlinedInput-root': { borderRadius: '10px' },
  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: PRIMARY }
};

const ReliabilitySubCategoryAdmin = () => {
  const [loading, setLoading] = useState(true);
  const [subCategories, setSubCategories] = useState([]);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogPillar, setDialogPillar] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', alias: '' });
  const [saving, setSaving] = useState(false);

  // Delete state
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const res = await get('/api/reliability/subcategories');
      if (res.success) setSubCategories(res.data || []);
      else setError('Failed to load sub-categories');
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const openCreate = (pillar) => {
    setEditing(null);
    setDialogPillar(pillar);
    setForm({ name: '', alias: '' });
    setDialogOpen(true);
  };

  const openEdit = (sub) => {
    const pillar = PILLARS.find(p => p.key === sub.pillarKey);
    setEditing(sub);
    setDialogPillar(pillar);
    setForm({ name: sub.name, alias: sub.alias });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.alias.trim()) {
      setError('Name and alias are required');
      return;
    }
    try {
      setSaving(true); setError(null);
      const res = editing
        ? await authPut(`/api/reliability/subcategories/${editing._id}`, { name: form.name, alias: form.alias })
        : await authPost('/api/reliability/subcategories', {
            pillarKey: dialogPillar.key,
            name: form.name,
            alias: form.alias
          });
      if (res.success) {
        setSuccess(editing ? 'Sub-category updated.' : 'Sub-category created.');
        setTimeout(() => setSuccess(null), 4000);
        setDialogOpen(false);
        loadData();
      } else {
        setError(res.message || 'Failed to save');
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      setDeleting(true);
      await authDelete(`/api/reliability/subcategories/${deleteTarget._id}`);
      setDeleteTarget(null);
      loadData();
    } catch (e) {
      setError(e.message);
    } finally {
      setDeleting(false);
    }
  };

  const byPillar = (key) => subCategories.filter(s => s.pillarKey === key);

  return (
    <Box>
      {/* Header */}
      <Paper elevation={0} sx={{ p: 2, mb: 3, borderRadius: '16px', border: '1.5px solid rgba(0,0,0,0.08)' }}>
        <Typography variant="body1" fontWeight={600} color={PRIMARY}>
          Reliability Sub-categories
        </Typography>
        <Typography variant="caption" color={MUTED} display="block" mt={0.25}>
          Sub-categories appear under each pillar in the Product Engineering tab.
          Tag ADO Features with <code>reliability-category-&lt;alias&gt;</code> to link them.
        </Typography>
      </Paper>

      {success && <Alert severity="success" sx={{ mb: 2, borderRadius: '10px' }}>{success}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2, borderRadius: '10px' }} onClose={() => setError(null)}>{error}</Alert>}

      {loading ? (
        <Box display="flex" justifyContent="center" p={6}><CircularProgress sx={{ color: PRIMARY }} /></Box>
      ) : (
        <Box display="flex" flexDirection="column" gap={2.5}>
          {PILLARS.map(pillar => {
            const Icon = pillar.icon;
            const subs = byPillar(pillar.key);
            return (
              <Paper
                key={pillar.key}
                elevation={0}
                sx={{ borderRadius: '16px', overflow: 'hidden', border: '1.5px solid rgba(0,0,0,0.08)' }}
              >
                {/* Pillar header */}
                <Box sx={{
                  px: 2.5, py: 2,
                  bgcolor: `${pillar.color}0d`,
                  display: 'flex', alignItems: 'center', gap: 1.5
                }}>
                  <Box sx={{
                    width: 38, height: 38, borderRadius: '10px',
                    bgcolor: `${pillar.color}1e`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
                  }}>
                    <Icon sx={{ color: pillar.color, fontSize: 20 }} />
                  </Box>
                  <Typography variant="body1" fontWeight={600} color="#1a1a1a" flex={1}>
                    {pillar.label}
                  </Typography>
                  <Chip
                    label={`${subs.length} sub-categor${subs.length !== 1 ? 'ies' : 'y'}`}
                    size="small"
                    sx={{ bgcolor: `${pillar.color}18`, color: pillar.color, fontWeight: 600, fontSize: '0.75rem' }}
                  />
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => openCreate(pillar)}
                    sx={{
                      textTransform: 'none', fontWeight: 600, borderRadius: '8px',
                      color: pillar.color, borderColor: `${pillar.color}60`,
                      '&:hover': { bgcolor: `${pillar.color}0d` }
                    }}
                    variant="outlined"
                  >
                    Add
                  </Button>
                </Box>

                {/* Sub-category list */}
                {subs.length === 0 ? (
                  <Box px={3} py={2.5}>
                    <Typography variant="body2" color={MUTED}>
                      No sub-categories yet. Click Add to create one.
                    </Typography>
                  </Box>
                ) : (
                  <Box>
                    {subs.map((sub, idx) => (
                      <React.Fragment key={sub._id}>
                        {idx > 0 && <Divider />}
                        <Box
                          display="flex" alignItems="center" gap={2}
                          px={2.5} py={1.5}
                          sx={{ bgcolor: idx % 2 === 1 ? 'rgba(0,0,0,0.015)' : 'transparent' }}
                        >
                          <Box flex={1} minWidth={0}>
                            <Typography variant="body2" fontWeight={500}>{sub.name}</Typography>
                            <Chip
                              label={`reliability-category-${sub.alias}`}
                              size="small"
                              sx={{
                                mt: 0.4, bgcolor: `${pillar.color}12`,
                                color: pillar.color, fontWeight: 600,
                                fontSize: '0.7rem', fontFamily: 'monospace', height: 20
                              }}
                            />
                          </Box>
                          <Box display="flex" gap={0.5} flexShrink={0}>
                            <Tooltip title="Edit">
                              <IconButton size="small" onClick={() => openEdit(sub)}
                                aria-label={`Edit ${sub.name}`}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            {isAdmin() && (
                              <Tooltip title="Delete">
                                <IconButton size="small" color="error"
                                  onClick={() => setDeleteTarget(sub)}
                                  aria-label={`Delete ${sub.name}`}>
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </Box>
                      </React.Fragment>
                    ))}
                  </Box>
                )}
              </Paper>
            );
          })}
        </Box>
      )}

      {/* Create / Edit dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: '20px' } }}>
        <DialogTitle sx={{ fontWeight: 700, color: PRIMARY, pb: 1 }}>
          {editing ? 'Edit Sub-category' : `Add Sub-category — ${dialogPillar?.label}`}
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
          <TextField
            label="Sub-category Name *"
            value={form.name}
            onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
            fullWidth size="small" sx={inputSx}
            placeholder="e.g. Database Performance & Monitoring"
            autoFocus
          />
          <TextField
            label="Alias *"
            value={form.alias}
            onChange={e => setForm(p => ({ ...p, alias: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') }))}
            fullWidth size="small" sx={inputSx}
            placeholder="e.g. db-perf"
            helperText={`ADO tag: reliability-category-${form.alias || '<alias>'}`}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}
            sx={{ textTransform: 'none', borderRadius: '10px', color: MUTED }}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}
            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '10px', bgcolor: PRIMARY, '&:hover': { bgcolor: PRIMARY_DARK } }}>
            {saving ? 'Saving…' : editing ? 'Save Changes' : 'Add Sub-category'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}
        PaperProps={{ sx: { borderRadius: '20px' } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>Delete Sub-category</DialogTitle>
        <DialogContent>
          <Typography>
            Delete <strong>"{deleteTarget?.name}"</strong>?<br />
            Features tagged with <code>reliability-category-{deleteTarget?.alias}</code> will
            appear in General until retagged.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}
            sx={{ textTransform: 'none', borderRadius: '10px', color: MUTED }}>
            Cancel
          </Button>
          <Button variant="contained" color="error" onClick={handleDelete} disabled={deleting}
            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '10px' }}>
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ReliabilitySubCategoryAdmin;
